import React, { useState } from 'react';
import { ProfileService } from '../../service/Profile.service';
import { ApiService } from '@/shared/service/Apiservice';
import axios from 'axios';
import './styles/CancelOrderModal.css';

interface CancelOrderModalProps {
  orderId: string;
  orderStatus: string;
  trackingStatus?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CANCELLATION_REASONS = [
  { id: 'found_better_price', label: 'Found a better price' },
  { id: 'order_placed_by_mistake', label: 'Order placed by mistake' },
  { id: 'delivery_time_too_long', label: 'Delivery time too long' },
  { id: 'changed_mind', label: 'Changed my mind' },
  { id: 'other', label: 'Other' }
];

const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  orderId,
  orderStatus,
  trackingStatus,
  onClose,
  onSuccess
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const apiService = React.useMemo(() => new ApiService(axios.create()), []);
  const profileService = React.useMemo(() => new ProfileService(apiService), [apiService]);
  const canCancel = ['pending', 'paid'].includes(orderStatus?.toLowerCase())
    && trackingStatus?.toLowerCase() !== 'out_for_delivery';

  const handleCancel = async () => {
    if (!selectedReason) {
      setError('Please select a cancellation reason');
      return;
    }

    if (selectedReason === 'other' && !customReason.trim()) {
      setError('Please provide a custom reason');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await profileService.cancelOrder(
        orderId,
        selectedReason,
        selectedReason === 'other' ? customReason : undefined
      );

      if (response?.success || response?.order) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      } else {
        setError(response?.message || 'Failed to cancel order. Please try again.');
      }
    } catch (err: any) {
      setError(err?.message || 'An error occurred while cancelling the order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cancel-order-modal-overlay" onClick={onClose}>
      <div className="cancel-order-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cancel-modal-header">
          <h2>Cancel Order</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="cancel-modal-content">
          {!canCancel ? (
            <div className="cancel-error-message">
              <p>This order cannot be cancelled. Current status: <strong>{orderStatus}</strong></p>
              <p className="cancel-info-text">Orders can only be cancelled when they are in 'pending' or 'paid' status and not out for delivery.</p>
            </div>
          ) : success ? (
            <div className="cancel-success-message">
              <div className="success-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="32" cy="32" r="30" fill="#10B981" fillOpacity="0.1" stroke="#10B981" strokeWidth="2"/>
                  <path d="M44 24L28 40L20 32" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Order Cancelled Successfully</h3>
              <p>Your order has been cancelled. A confirmation email has been sent to you.</p>
              {['paid'].includes(orderStatus?.toLowerCase()) && (
                <p className="refund-info">Your refund will be processed within 2-3 business days.</p>
              )}
            </div>
          ) : (
            <>
              <p className="cancel-modal-description">
                Please select a reason for cancellation:
              </p>

              <div className="cancel-reasons-list">
                {CANCELLATION_REASONS.map((reason) => (
                  <label key={reason.id} className="reason-option">
                    <input
                      type="radio"
                      name="cancel-reason"
                      value={reason.id}
                      checked={selectedReason === reason.id}
                      onChange={(e) => {
                        setSelectedReason(e.target.value);
                        setError(null);
                      }}
                    />
                    <span className="reason-label">{reason.label}</span>
                  </label>
                ))}
              </div>

              {selectedReason === 'other' && (
                <div className="custom-reason-input-wrapper">
                  <textarea
                    placeholder="Please tell us why you want to cancel this order..."
                    value={customReason}
                    onChange={(e) => {
                      setCustomReason(e.target.value);
                      setError(null);
                    }}
                    rows={4}
                    maxLength={500}
                    className="custom-reason-input"
                  />
                  <div className="char-count">{customReason.length}/500</div>
                </div>
              )}

              {error && (
                <div className="cancel-error-message">
                  {error}
                </div>
              )}

              <div className="cancel-modal-footer">
                <button
                  className="cancel-modal-btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Keep Order
                </button>
                <button
                  className="cancel-modal-btn-primary"
                  onClick={handleCancel}
                  disabled={loading || !selectedReason}
                >
                  {loading ? 'Cancelling...' : 'Cancel Order'}
                </button>
              </div>

              <p className="cancel-info-text">
                Due to the nature of rapid delivery, cancellations may still be charged the complete order amount.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancelOrderModal;
